import { addBreadcrumb, captureMessage } from '@sentry/react';

import { safeCallAsync } from '@xemplo/safe-call';
import { XemploSessionData } from '@xemplo/types';

const SESSION_STATE_BREADCRUMB = 'session-state';
const SESSION_STATE_PATH = `api/v2/state/session`;

/**
 * Upper bound on the one blocking request of the bootstrap. Generous, because a
 * cold backend legitimately takes seconds — but bounded, because a request that
 * never settles (a blocked preflight, say) would otherwise leave the app on its
 * loader forever with no way out.
 */
const SESSION_FETCH_TIMEOUT_MS = 20_000;

// Module-level cache: guarantees the fetch fires exactly once regardless of
// React StrictMode double-invocation or concurrent-mode re-renders.
let sessionPromise: Promise<void> | null = null;

// undefined = not yet settled; null = settled but failed / no dsUrl;
// XemploSessionData = settled with data.
let sessionData: XemploSessionData | null | undefined = undefined;

/** Clears the module-level state. Call in beforeEach in tests. */
export function _resetForTesting() {
  sessionPromise = null;
  sessionData = undefined;
}

function getSessionPromise(dsUrl: string): Promise<void> {
  sessionPromise ??= resolveSessionData(dsUrl);
  return sessionPromise;
}

/**
 * Settles `sessionData` exactly once, leaving a breadcrumb for each way the
 * record can fail to name a persona. Both ways end with the user on the
 * persona-selection page, which is otherwise indistinguishable from choice.
 */
async function resolveSessionData(dsUrl: string): Promise<void> {
  const [data, error] = await safeCallAsync(fetchSession(dsUrl));

  if (error || !data) {
    // Non-blocking telemetry so this silent degradation is observable: the
    // cross-app bootstrap could not establish a persona.
    addBreadcrumb({
      category: SESSION_STATE_BREADCRUMB,
      level: 'error',
      message: 'Session-state fetch failed',
      data: { reason: error?.message ?? 'no data' },
    });
    sessionData = null;
    return;
  }

  if (!data.personaId) reportPersonaLessRecord(data);

  sessionData = data;
}

/**
 * A record with no persona sends the user to persona selection, so it needs to be
 * visible — but before anyone has picked a persona it is the expected state, and
 * capturing it every time would be pure noise. An `instanceUrl` without a
 * `personaId` is the anomaly: a persona was selected at some point and the two
 * halves of the record have since disagreed. Only that gets its own event; the
 * ordinary case leaves a breadcrumb for whatever error reports next.
 */
function reportPersonaLessRecord(data: XemploSessionData) {
  const isInconsistent = data.instanceUrl != null;

  addBreadcrumb({
    category: SESSION_STATE_BREADCRUMB,
    level: 'warning',
    message: 'Session-state record carried no persona',
    data: { isInconsistent },
  });

  if (!isInconsistent) return;

  captureMessage('Session-state record has an instance URL but no persona', {
    level: 'warning',
    tags: { flow: SESSION_STATE_BREADCRUMB },
  });
}

async function fetchSession(dsUrl: string): Promise<XemploSessionData> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SESSION_FETCH_TIMEOUT_MS);

  // `new URL` normalises the join so a trailing slash on dsUrl can't produce `//`.
  const [response, error] = await safeCallAsync(
    fetch(new URL(SESSION_STATE_PATH, dsUrl).toString(), {
      method: 'GET',
      /**
       * withCredentials is required to ensure that cookies are stored and are sent with requests,
       * Allows browsers to accept Set-Cookie headers from the response
       * This informs BE of the existing cookie in the browser and prevents from generating new session id.
       */
      credentials: 'include',
      signal: controller.signal,
    }),
  );
  clearTimeout(timeoutId);

  if (!response?.ok) {
    throw new Error(error?.message ?? `Failed to fetch session: ${response?.statusText}`);
  }

  return response.json();
}

/**
 * Reads the cross-app session record, suspending until the one-time
 * `/api/v2/state/session` fetch settles. Returns `null` when it failed.
 *
 * This is the only source of truth for the active persona (ADR 0013), so it is
 * read *above* `XemploAppSettingsProvider` and seeded into it. Applying it from
 * an effect below the provider used to leave a committed render holding the
 * tab's previous persona, which is what `withSelectedPersona` then pinned.
 *
 * ⚠️ Notes:
 * - Must be called inside a React Suspense boundary.
 * - React Query is intentionally not used here.
 * - The request does NOT require an authentication token.
 * - The endpoint sets a session cookie used for session management.
 * - Fetch errors are swallowed; the app degrades to the persona-selection path.
 */
export function useSessionRecord(dsUrl: string): XemploSessionData | null {
  // Throw the Promise while it is in-flight: React catches it, shows the Suspense
  // fallback, and retries this component once the Promise resolves.
  if (sessionData === undefined) throw getSessionPromise(dsUrl);

  return sessionData;
}
